'use client';

import { Loading } from '@jouzen/ecom-components/v3/Loading';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { Provider } from 'react-redux';

import {
  type AppStore,
  hydrate,
  loadPersistedState,
  makeStore,
} from '@/lib/store';

export default function StoreProvider({
  children,
}: {
  readonly children: ReactNode;
}) {
  // React 19 requires an explicit initial value for useRef.
  const storeRef = useRef<AppStore | undefined>(undefined);
  if (!storeRef.current) {
    // Create the store instance the first time this renders. It boots from
    // the empty initial state so the server render and the client's first
    // render produce identical markup (fixes React error #423).
    storeRef.current = makeStore();
  }

  // The store is populated from localStorage after mount rather than
  // synchronously at creation. Children are held back until that happens so
  // store-derived redirects (e.g. useBusinessRedirect) never evaluate an
  // empty store and bounce the user to an earlier step before rehydration.
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    try {
      const persistedState = loadPersistedState();
      if (persistedState) {
        storeRef.current?.dispatch(hydrate(persistedState));
      }
    } finally {
      // Always reveal the app, even if rehydration failed, so a bad
      // persisted snapshot can never leave the user stuck on the shell.
      setIsHydrated(true);
    }
  }, []);

  // Render a stable loading shell (identical on the server and the client's
  // first render) until rehydration completes, then swap in the app.
  //
  // NOTE: StoreProvider wraps the whole app (src/app/[locale]/layout.tsx),
  // so gating here means every route's server-rendered HTML is this shell —
  // SSR of page content is intentionally disabled. That's an accepted
  // tradeoff: this is an authed order portal with no indexable routes, and
  // the order data lives in localStorage (never available to the server
  // anyway), so SSR could only ever render the static shell. Holding
  // children back also keeps store-derived redirects from running against
  // the empty pre-hydration store. If public/SEO routes are ever added,
  // revisit this: render children immediately and gate redirects on an
  // explicit hydration flag instead.
  if (!isHydrated) {
    return <Loading fullScreen />;
  }

  return <Provider store={storeRef.current}>{children}</Provider>;
}
